import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { IOtapUpgradeTaskInputParametersDTO } from '@app/shared/models/otap-upgrade-task.interface';

const ENDPOINT_MAX = 255;
const NODE_ADDRESS_MAX = 4294967295;
const BYTE_MAX = 0xff;
const WORD_MAX = 0xffff;
const UNBOUNDED_MAX = Number.MAX_SAFE_INTEGER;

const AUTO_SEQUENCE = 'auto';
const NON_NEGATIVE_INTEGER_PATTERN = /^\d+$/;
const HEX_REFERENCE_PATTERN = /^0[xX][0-9a-fA-F]+$/;
const HEX_TOKEN_PATTERN = /^[0-9a-fA-F]{1,8}$/;
const HEX_COMMAND_PATTERN = /^([0-9a-fA-F]{2})+$/;
const URL_PATTERN = /^https?:\/\/\S+$/i;

const OPTIONAL_EXPECTED_VALUES = [
  'STACK_VERSION',
  'CRC',
  'SEQ'
];

export function isOtapTaskInputRequired(parameter: IOtapUpgradeTaskInputParametersDTO, taskType: string): boolean {
  return !(taskType === 'QUERY_NORDIC_SCRATCHPAD_STATUS' && OPTIONAL_EXPECTED_VALUES.includes(parameter.name));
}

export function getOtapTaskInputValidators(
  parameter: IOtapUpgradeTaskInputParametersDTO,
  taskType: string
): ValidatorFn[] {
  const validators: ValidatorFn[] = [];
  if (isOtapTaskInputRequired(parameter, taskType)) {
    validators.push(Validators.required);
  }

  switch (parameter.name) {
    case 'SOURCE_ENDPOINT':
    case 'DESTINATION_ENDPOINT':
      validators.push(integerInRangeValidator(ENDPOINT_MAX));
      break;
    case 'DESTINATION':
      validators.push(separatedIntegersInRangeValidator(NODE_ADDRESS_MAX));
      break;
    case 'DESTINATION_HEX':
      validators.push(separatedHexTokensValidator());
      break;
    case 'TIME_OUT':
    case 'TIME_OUT_ITERATIONS':
    case 'ACTION':
    case 'ACTION_DELAY':
      validators.push(integerInRangeValidator(UNBOUNDED_MAX));
      break;
    case 'DELAY':
      validators.push(integerInRangeValidator(BYTE_MAX));
      break;
    case 'SEQ':
      validators.push(sequenceValidator(taskType));
      break;
    case 'CRC':
      validators.push(
        taskType === 'QUERY_NORDIC_SCRATCHPAD_STATUS'
          ? referenceInRangeValidator(WORD_MAX)
          : integerInRangeValidator(WORD_MAX)
      );
      break;
    case 'HEX_COMMAND':
      validators.push(patternValidator(HEX_COMMAND_PATTERN, 'Enter a hex string with an even number of hex digits'));
      break;
    case 'URL':
      validators.push(patternValidator(URL_PATTERN, 'Enter a valid http(s) URL'));
      break;
    default:
      break;
  }

  return validators;
}

function sequenceValidator(taskType: string): ValidatorFn {
  switch (taskType) {
    case 'INITIALIZE_PIC_OTAP':
    case 'START_UPDATE_COMMAND':
    case 'SEND_NORDIC_UPDATE':
      return integerInRangeValidator(BYTE_MAX);
    case 'QUERY_NORDIC_SCRATCHPAD_STATUS':
      return referenceInRangeValidator(BYTE_MAX);
    default: {
      const wholeNumberValidator = integerInRangeValidator(BYTE_MAX, `Enter "${AUTO_SEQUENCE}" or a whole number between 0 and ${BYTE_MAX}`);
      return (control: AbstractControl): ValidationErrors | null => {
        return normalize(control.value).toLowerCase() === AUTO_SEQUENCE ? null : wholeNumberValidator(control);
      };
    }
  }
}

function integerInRangeValidator(max: number, message?: string): ValidatorFn {
  const invalidValue = { invalidValue: message ?? `Enter 0-${max}` };
  return (control: AbstractControl): ValidationErrors | null => {
    const value = normalize(control.value);
    if (value === '') {
      return null;
    }
    return NON_NEGATIVE_INTEGER_PATTERN.test(value) && Number(value) <= max ? null : invalidValue;
  };
}

function referenceInRangeValidator(max: number): ValidatorFn {
  const invalidValue = { invalidValue: `Enter 0-${max} or 0x hex` };
  return (control: AbstractControl): ValidationErrors | null => {
    const value = normalize(control.value);
    if (value === '') {
      return null;
    }
    if (HEX_REFERENCE_PATTERN.test(value)) {
      return parseInt(value.substring(2), 16) <= max ? null : invalidValue;
    }
    return NON_NEGATIVE_INTEGER_PATTERN.test(value) && Number(value) <= max ? null : invalidValue;
  };
}

function separatedIntegersInRangeValidator(max: number): ValidatorFn {
  const invalidValue = { invalidValue: `Enter 0-${max}, space-separated` };
  return (control: AbstractControl): ValidationErrors | null => {
    const value = normalize(control.value);
    if (value === '') {
      return null;
    }
    const valid = value.split(' ').every(token => NON_NEGATIVE_INTEGER_PATTERN.test(token) && Number(token) <= max);
    return valid ? null : invalidValue;
  };
}

function separatedHexTokensValidator(): ValidatorFn {
  const invalidValue = { invalidValue: 'Enter 1-8 hex digits, space-separated' };
  return (control: AbstractControl): ValidationErrors | null => {
    const value = normalize(control.value);
    if (value === '') {
      return null;
    }
    return value.split(' ').every(token => HEX_TOKEN_PATTERN.test(token)) ? null : invalidValue;
  };
}

function patternValidator(pattern: RegExp, message: string): ValidatorFn {
  const invalidValue = { invalidValue: message };
  return (control: AbstractControl): ValidationErrors | null => {
    const value = normalize(control.value);
    if (value === '') {
      return null;
    }
    return pattern.test(value) ? null : invalidValue;
  };
}

function normalize(value: unknown): string {
  return value == null ? '' : String(value).trim().replace(/\s+/g, ' ');
}
